/**
 * Ant Design Theme — RLC brand
 *
 * Ant Design styles roughly 56% of this app (59 of 106 components), so this file
 * is the single highest-leverage surface in the rebrand. CSS-file migration does
 * not touch any of it.
 *
 * Values come from `rlcThemeTokens.js`, generated from `brand/rlc/tokens.json`.
 * Do not hardcode colors here — that is how this file ended up holding #0f1770
 * eight times, silently diverging from brandColors.css.
 *
 * Ant Design derives entire palettes from these base colors, so it needs literal
 * hex values; `var(--rlc-…)` would break the derivation. That is the only reason
 * a JS token module exists alongside the CSS one.
 *
 * PHASE C: flip `ACTIVE` in rlcThemeTokens.js from LEGACY to RLC.
 */

import { ACTIVE as C, RADIUS } from './rlcThemeTokens';

const px = (value) => parseInt(value, 10);

export const antdTheme = {
  token: {
    // Primary action. Note this is the CTA, not the nav colour — the two were
    // the same value (#0f1770) before and are deliberately separated now, so
    // the nav can be Dark Teal while the CTA is Signal Orange.
    colorPrimary: C['action-primary'],

    colorLink: C['text-link'],
    colorLinkHover: C['text-link-hover'],

    // Surfaces
    colorBgContainer: C['surface'],
    colorBgLayout: C['surface-sunken'],
    colorBgElevated: C['surface-raised'],

    // Borders
    colorBorder: C['border-default'],
    colorBorderSecondary: C['border-subtle'],
    borderRadius: px(RADIUS.lg),
    borderRadiusLG: px(RADIUS.lg),
    borderRadiusSM: px(RADIUS.sm),

    // Text
    colorText: C['text-primary'],
    colorTextSecondary: C['text-secondary'],
    colorTextTertiary: C['text-muted'],

    // antd resolves this into its own stylesheets, so it needs a literal stack
    // rather than var(--rlc-font-ui). Kept in sync with tokens.json by hand —
    // it is one string, and a generated import here would not earn its keep.
    fontFamily: `'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,

    // States
    colorSuccess: C['state-success'],
    colorSuccessBg: C['state-success-bg'],
    colorWarning: C['state-warning'],
    colorWarningBg: C['state-warning-bg'],
    colorError: C['state-error'],
    colorErrorBg: C['state-error-bg'],
    colorInfo: C['state-info'],
    colorInfoBg: C['state-info-bg'],

    // Spacing
    margin: 16,
    marginLG: 24,
    marginXL: 32,
    padding: 16,
    paddingLG: 24,
    paddingXL: 32,
  },

  components: {
    Button: {
      primaryColor: C['action-primary-text'],
      colorPrimary: C['action-primary'],
      colorPrimaryHover: C['action-primary-hover'],
      colorPrimaryActive: C['action-primary-hover'],
      borderRadius: px(RADIUS.md),
    },

    // Nav chrome uses the inverse surface — Dark Teal after Phase C, per the
    // style guide's "nav bar, footer, dark section backgrounds".
    Layout: {
      siderBg: C['surface-inverse'],
      triggerBg: C['surface-inverse-alt'],
      headerBg: C['surface-inverse'],
    },

    Menu: {
      darkItemBg: C['surface-inverse'],
      darkItemSelectedBg: C['surface-inverse-alt'],
      darkItemHoverBg: C['surface-inverse-alt'],
    },

    Input: {
      borderRadius: px(RADIUS.sm),
      paddingBlock: 8,
      paddingInline: 12,
    },

    Card: {
      borderRadius: px(RADIUS.lg),
    },

    Message: {
      contentBg: C['surface-raised'],
      borderRadius: px(RADIUS.lg),
    },
  },
};

export default antdTheme;
